import React, { useState, useEffect } from 'react';
import svgPaths from "./svg-uz6vqub9fs";
import clsx from "clsx";
import { motion } from "motion/react";
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { updateTypography, resetBrandKit, TypographyState, TypographyVariant, setBrandKit } from '../../features/brand-kit/brandKitSlice';
import { useCreateBrandKitMutation, useGetBrandKitByIdQuery, useUpdateBrandKitMutation } from '../../features/brand-kit/brandKitApi';
import { CreateNewBrandKitModal } from './components/CreateNewBrandKitModal';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

// Constants
const FONTS = [
  "Bricolage Grotesque", "Abel", "Abril Fatface", "Abyssinica SIL", "Amatic SC", "Anton", "Archivo Narrow", "Arial",
  "Arial Black", "Arimo", "Arvo", "Asap", "Bitter", "Bookman", "Bree Serif", "Cabin", "Cairo",
  "Candal", "Cardo", "Carter One", "Catamaran", "Chivo", "Comfortaa", "Comic Sans MS", "Cookie",
  "Courier New", "Courgette", "Crete Round", "Crimson Text", "Cuprum", "Dancing Script",
  "DM Sans", "Dosis", "Droid Sans", "Droid Serif", "Exo 2", "Fjalla One", "Francois One",
  "Garamond", "Geist Mono", "Geologica", "Georgia", "Great Vibes", "Hammersmith One", "Helvetica",
  "Hind", "Impact", "Inconsolata", "Indie Flower", "Inter", "Josefin Sans", "Karla", "Kaushan Script",
  "Lato", "Libre Baskerville", "Lobster", "Lora", "Maven Pro", "Merriweather", "Montserrat", "Muli",
  "Noto Sans", "Noto Serif", "Nunito", "Old Standard TT", "Open Sans", "Open Sans Condensed",
  "Oswald", "Oxygen", "Pacifico", "Palatino", "Patua One", "Paytone One", "Permanent Marker",
  "Playfair Display", "Poiret One", "Pontano Sans", "Poppins", "PT Sans", "PT Serif", "Questrial",
  "Quicksand", "Raleway", "Righteous", "Roboto", "Roboto Condensed", "Roboto Slab", "Rokkitt",
  "Russo One", "Satisfy", "Shadows Into Light", "Signika", "Slabo 27px", "Source Sans Pro",
  "Times New Roman", "Tinos", "Titillium Web", "Trebuchet MS", "Ubuntu", "Ubuntu Condensed",
  "Varela", "Varela Round", "Verdana", "Vollkorn", "Yanone Kaffeesatz"
];
const SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 60, 64, 72, 80, 96];

// Reused Components from Figma Import (Modified for Interactivity)

type Wrapper5Props = {
  additionalClassNames?: string;
};

function Wrapper5({ children, additionalClassNames = "" }: React.PropsWithChildren<Wrapper5Props>) {
  return (
    <div style={{ fontVariationSettings: "'opsz' 14" }} className={clsx("flex flex-col font-sans font-semibold justify-center leading-[0] relative shrink-0 text-[#101928] w-full", additionalClassNames)}>
      <p className="leading-[1.8]">{children}</p>
    </div>
  );
}

function Wrapper4({ children, onClick, isActive }: React.PropsWithChildren<{ onClick?: () => void, isActive?: boolean }>) {
  return (
    <div
      className={clsx(
        "relative rounded-[4px] shrink-0 size-[40px] transition-all",
        onClick && "cursor-pointer",
        isActive ? "bg-gradient-to-b from-[rgba(11,162,155,0.08)] to-[rgba(67,180,141,0.08)] [&_path]:stroke-[#227735]" : "hover:bg-gray-50"
      )}
      onClick={onClick}
    >
      <div className="content-stretch flex items-center justify-center overflow-clip p-[12px] relative rounded-[inherit] size-full">{children}</div>
      <div aria-hidden="true" className={clsx("absolute border-solid inset-0 pointer-events-none rounded-[4px]", isActive ? "border-[#0ba29b] border-[0.5px]" : "border border-[#e4e7ec]")} />
    </div>
  );
}

const Wrapper3 = React.forwardRef<HTMLDivElement, React.PropsWithChildren<{ onClick?: () => void }>>(
  ({ children, onClick }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx("relative rounded-[4px] shrink-0 w-[190px]", onClick && "cursor-pointer hover:bg-gray-50 transition-colors")}
        onClick={onClick}
      >
        <div className="content-stretch flex items-center justify-between overflow-clip p-[12px] relative rounded-[inherit] w-full">{children}</div>
        <div aria-hidden="true" className="absolute border border-[#e4e7ec] border-solid inset-0 pointer-events-none rounded-[4px]" />
      </div>
    );
  }
);
Wrapper3.displayName = "Wrapper3";

function Wrapper2({ children }: React.PropsWithChildren) {
  return (
    <div className="relative shrink-0 size-[20px]">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
        {children}
      </svg>
    </div>
  );
}

function Wrapper1({ children }: React.PropsWithChildren) {
  return (
    <div className="basis-0 grow min-h-px min-w-px relative rounded-[8px] shrink-0 shadow-sm bg-white">
      <div className="flex flex-col justify-center overflow-clip rounded-[inherit] w-full">
        <div className="content-stretch flex flex-col gap-[16px] items-start justify-center px-[16px] py-[20px] relative w-full">{children}</div>
      </div>
      <div aria-hidden="true" className="absolute border-[#e4e7ec] border-[0.75px] border-solid inset-0 pointer-events-none rounded-[8px]" />
    </div>
  );
}

function Wrapper({ children }: React.PropsWithChildren) {
  return (
    <Wrapper2>
      <g id="Frame">{children}</g>
    </Wrapper2>
  );
}
type Text3Props = {
  text: string;
  additionalClassNames?: string;
};

function Text3({ text, additionalClassNames = "" }: Text3Props) {
  return (
    <div style={{ fontVariationSettings: "'CRSV' 0, 'SHRP' 0" }} className={clsx("flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[#101928] text-[16px]", additionalClassNames)}>
      <p className="leading-[1.8]">{text}</p>
    </div>
  );
}
type Text2Props = {
  text: string;
  onClick?: () => void;
};

function Text2({ text, onClick }: Text2Props) {
  return (
    <Wrapper3 onClick={onClick}>
      <div className="flex flex-col font-sans font-normal justify-center leading-[0] relative shrink-0 text-[#101928] text-[16px] text-nowrap">
        <p className="leading-[1.8]">{text}</p>
      </div>
      <Frame />
    </Wrapper3>
  );
}
type Text1Props = {
  text: string;
};

function Text1({ text }: Text1Props) {
  return (
    <div className="content-stretch flex flex-col items-start relative shrink-0">
      <div className="flex flex-col font-sans font-semibold justify-center leading-[0] relative shrink-0 text-[#101928] text-[18px] w-full">
        <p className="leading-[1.8]">{text}</p>
      </div>
    </div>
  );
}

function Frame1() {
  return (
    <Wrapper>
      <path d={svgPaths.p39daa180} id="Vector" stroke="var(--stroke-0, #101928)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      <path d="M18.333 7.5V13.3333" id="Vector_2" stroke="var(--stroke-0, #101928)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      <path d="M2.75293 10.8333H8.0796" id="Vector_3" stroke="var(--stroke-0, #101928)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      <path d={svgPaths.p148a6c00} id="Vector_4" stroke="var(--stroke-0, #101928)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
    </Wrapper>
  );
}

function Helper1({ onClick, isActive }: { onClick?: () => void, isActive?: boolean }) {
  return (
    <Wrapper4 onClick={onClick} isActive={isActive}>
      <Wrapper>
        <path d="M15.833 3.33325H8.33301" id="Vector" stroke="var(--stroke-0, #101928)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
        <path d="M11.667 16.6667H4.16699" id="Vector_2" stroke="var(--stroke-0, #101928)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
        <path d="M12.5 3.33333L7.5 16.6667" id="Vector_3" stroke="var(--stroke-0, #101928)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      </Wrapper>
    </Wrapper4>
  );
}

function Helper({ onClick, isActive }: { onClick?: () => void, isActive?: boolean }) {
  return (
    <Wrapper4 onClick={onClick} isActive={isActive}>
      <Wrapper>
        <path d={svgPaths.p3603580} id="Vector" stroke="var(--stroke-0, #101928)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      </Wrapper>
    </Wrapper4>
  );
}
type TextProps = {
  text: string;
  additionalClassNames?: string;
  onClick?: () => void;
};

function Text({ text, additionalClassNames = "", onClick }: TextProps) {
  return (
    <div
      className={clsx("relative rounded-[4px] shrink-0", additionalClassNames, onClick && "cursor-pointer hover:bg-gray-50 transition-colors")}
      onClick={onClick}
    >
      <div className="content-stretch flex items-center justify-between overflow-clip p-[12px] relative rounded-[inherit] w-full">
        <div className="flex flex-col font-sans font-normal justify-center leading-[0] relative shrink-0 text-[#101928] text-[16px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14" }}>
          <p className="leading-[1.8]">{text}</p>
        </div>
        <Frame />
      </div>
      <div aria-hidden="true" className="absolute border border-[#e4e7ec] border-solid inset-0 pointer-events-none rounded-[4px]" />
    </div>
  );
}

function Frame() {
  return (
    <div className="relative shrink-0 size-[16px]">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 16 16">
        <g id="Frame">
          <path d="M4 6L8 10L12 6" id="Vector" stroke="var(--stroke-0, #475367)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
        </g>
      </svg>
    </div>
  );
}


// Logic Containers
function FontFamilySelector({
  font,
  onChange,
  triggerComponent
}: {
  font: string,
  onChange: (f: string) => void,
  triggerComponent: React.ReactNode
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        {triggerComponent}
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-1" align="start">
        <div className="flex flex-col max-h-[300px] overflow-y-auto [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:bg-[#D0D5DD] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
          {FONTS.map(f => (
            <div
              key={f}
              className={clsx("px-3 py-2 hover:bg-gray-100 cursor-pointer rounded text-sm", font === f && "bg-gray-50 font-medium text-[#2c9944]")}
              onClick={() => onChange(f)}
              style={{ fontFamily: f }}
            >
              {f}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FontSizeSelector({
  size,
  onChange,
  className = "w-[120px]"
}: {
  size: number,
  onChange: (s: number) => void,
  className?: string
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <div>
          <Text text={size.toString()} additionalClassNames={className} onClick={() => { }} />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[120px] p-1" align="start">
        <div className="flex flex-col max-h-[200px] overflow-y-auto [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:bg-[#D0D5DD] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
          {SIZES.map(s => (
            <div
              key={s}
              className={clsx("px-3 py-2 hover:bg-gray-100 cursor-pointer rounded text-sm", size === s && "bg-gray-50 font-medium text-[#2c9944]")}
              onClick={() => onChange(s)}
            >
              {s}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}


export default function CreateBrandKitTypography() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const brandKitName = useAppSelector(state => state.brandKit.name);
  const typography = useAppSelector(state => state.brandKit.typography);
  const [isBrandKitModalOpen, setIsBrandKitModalOpen] = useState(false);

  const brandKitState = useAppSelector(state => state.brandKit);
  const [createBrandKit, { isLoading: isCreating }] = useCreateBrandKitMutation();
  const [updateBrandKitMutation, { isLoading: isUpdating }] = useUpdateBrandKitMutation();

  const baseUrl = id ? `/brand-kit/edit/${id}` : '/brand-kit/create';
  const { data: brandKitData } = useGetBrandKitByIdQuery(id!, { skip: !id });

  useEffect(() => {
    if (brandKitData && id) {
      dispatch(setBrandKit(brandKitData));
    }
  }, [brandKitData, id, dispatch]);

  const handleCreateBrandKit = async () => {
    if (isCreating) return;
    try {
      await createBrandKit(brandKitState).unwrap();
      dispatch(resetBrandKit());
      navigate('/brand-kit');
    } catch (error) {
      console.error('Failed to create brand kit:', error);
    }
  };

  const handleUpdateBrandKit = async () => {
    if (isUpdating || !id) return;
    try {
      await updateBrandKitMutation({ id, data: brandKitState }).unwrap();
      dispatch(resetBrandKit());
      navigate('/brand-kit');
    } catch (error) {
      console.error('Failed to update brand kit:', error);
    }
  };

  const isLoading = isCreating || isUpdating;

  // Fallback to avoid crashes if redux state isn't ready



  const handleTypographyChange = async (type: keyof TypographyState, changes: Partial<TypographyVariant>) => {
    const current = typography[type];
    const newChanges = { ...changes };

    // Handle mutual exclusivity for style toggles
    if ('isBold' in changes || 'isItalic' in changes || 'isUppercase' in changes) {
      if (changes.isBold) {
        newChanges.isItalic = false;
        newChanges.isUppercase = false;
      } else if (changes.isItalic) {
        newChanges.isBold = false;
        newChanges.isUppercase = false;
      } else if (changes.isUppercase) {
        newChanges.isBold = false;
        newChanges.isItalic = false;
      }

      // Check if we are turning something off and might end up with nothing
      const resultingBold = 'isBold' in newChanges ? newChanges.isBold : current.isBold;
      const resultingItalic = 'isItalic' in newChanges ? newChanges.isItalic : current.isItalic;
      const resultingUpper = 'isUppercase' in newChanges ? newChanges.isUppercase : current.isUppercase;

      if (!resultingBold && !resultingItalic && !resultingUpper) {
        newChanges.isBold = true;
      }
    }

    dispatch(updateTypography({ type, changes: newChanges }));

    if (id) {
      try {
        const nextTypography = {
          ...typography,
          [type]: { ...current, ...newChanges }
        };
        await updateBrandKitMutation({
          id,
          data: { ...brandKitState, typography: nextTypography }
        }).unwrap();
      } catch (error) {
        console.error('Failed to update typography:', error);
      }
    }
  };

  // Preview Style helper
  const getPreviewStyle = (variant: TypographyVariant) => {
    // Determine the actual bold state based on the "default to bold" requirement
    const actualBold = variant.isBold || (!variant.isItalic && !variant.isUppercase);

    return {
      fontFamily: variant.fontFamily,
      fontWeight: actualBold ? 700 : 400,
      fontStyle: variant.isItalic ? 'italic' : 'normal',
      textTransform: variant.isUppercase ? 'uppercase' : 'none',
    } as React.CSSProperties;
  };

  if (!typography) return null;

  return (
    <div className="bg-white relative size-full" data-name="Create Brand Kit - Typography">
      <CreateNewBrandKitModal
        isOpen={isBrandKitModalOpen}
        onClose={() => setIsBrandKitModalOpen(false)}
        mode="edit"
        id={id}
      />

      <div className="absolute bg-white h-[72px] left-[22px] right-[22px] rounded-[8px] top-[16px]">
        <div className="content-stretch flex items-center justify-between overflow-clip px-[16px] py-[12px] relative rounded-[inherit] size-full">
          <div className="content-stretch flex gap-[12px] items-center relative shrink-0">
            <Wrapper>
              <path d={svgPaths.p33f6b680} id="Vector" stroke="var(--stroke-0, #101928)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
              <path d="M15.8333 10H4.16667" id="Vector_2" stroke="var(--stroke-0, #101928)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
            </Wrapper>
            <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
              <div className="flex flex-col font-sans font-semibold justify-center leading-[0] relative shrink-0 text-[#101928] text-[16px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                <p className="leading-[normal]">{brandKitName}</p>
              </div>
              <div className="cursor-pointer" onClick={() => setIsBrandKitModalOpen(true)}>
                <Wrapper2>
                  <g clipPath="url(#clip0_108_720)" id="Frame">
                    <path d={svgPaths.p30d9cf00} id="Vector" stroke="var(--stroke-0, #344054)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                    <path d="M12.5 4.16667L15.8333 7.5" id="Vector_2" stroke="var(--stroke-0, #344054)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                  </g>
                  <defs>
                    <clipPath id="clip0_108_720">
                      <rect fill="white" height="20" width="20" />
                    </clipPath>
                  </defs>
                </Wrapper2>
              </div>
            </div>
          </div>
          <button
            className={`bg-[#30a84b] content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0 transition-colors ${isLoading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:bg-[#288e3e]'}`}
            data-name="Primary - Sm"
            onClick={isLoading ? undefined : (id ? handleUpdateBrandKit : handleCreateBrandKit)}
            disabled={isLoading}
          >
            <p className="font-sans font-semibold leading-[normal] relative shrink-0 text-[14px] text-nowrap text-white" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
              {isLoading ? (id ? 'Updating...' : 'Creating...') : (id ? 'Update Brand Kit' : 'Create Brand Kit')}
            </p>
          </button>
        </div>
        <div aria-hidden="true" className="absolute border-[#e4e7ec] border-[0.75px] border-solid inset-0 pointer-events-none rounded-[8px]" />
      </div>

      <div className="absolute content-stretch flex flex-col gap-[24px] items-start left-[22px] right-[22px] top-[120px] pb-20">
        <div className="flex gap-[12px] items-center relative shrink-0 bg-gray-50/50 p-1.5 rounded-xl border border-gray-100">
          <div
            className="relative shrink-0 min-w-[100px] h-[36px] flex items-center justify-center overflow-hidden rounded-lg group cursor-pointer"
            onClick={() => navigate(`${baseUrl}/logo`)}
          >
            <div 
                className="app-selector-indicator" 
                style={{ '--background-width': '0%' } as React.CSSProperties} 
            />
            <div className="flex items-center gap-2 relative z-10">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#667185] group-hover:text-[#30A84B] transition-colors">
                <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                <circle cx="9" cy="9" r="2" />
                <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
              </svg>
              <span className="font-sans font-medium text-[14px] text-[#667185] group-hover:text-[#30A84B] transition-colors">Logo</span>
            </div>
          </div>

          <div
            className="relative shrink-0 min-w-[100px] h-[36px] flex items-center justify-center overflow-hidden rounded-lg group cursor-pointer"
            onClick={() => navigate(`${baseUrl}/colors`)}
          >
            <div 
                className="app-selector-indicator" 
                style={{ '--background-width': '0%' } as React.CSSProperties} 
            />
            <div className="flex items-center gap-2 relative z-10">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#667185] group-hover:text-[#30A84B] transition-colors">
                <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
                <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
                <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
                <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
              </svg>
              <span className="font-sans font-medium text-[14px] text-[#667185] group-hover:text-[#30A84B] transition-colors">Colours</span>
            </div>
          </div>

          <div className="relative shrink-0 min-w-[119px] h-[36px] flex items-center justify-center overflow-hidden rounded-lg group">
            <div 
                className="app-selector-indicator" 
                style={{ '--background-width': '100%' } as React.CSSProperties} 
            />
            <div className="flex items-center gap-2 relative z-10">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#30A84B]">
                <polyline points="4 7 4 4 20 4 20 7" />
                <line x1="9" x2="15" y1="20" y2="20" />
                <line x1="12" x2="12" y1="4" y2="20" />
              </svg>
              <span className="font-sans font-medium text-[14px] app-active-state">Typography</span>
            </div>
          </div>
        </div>

        <motion.div
          className="content-stretch flex flex-col gap-[24px] items-start relative shrink-0 w-full"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* ROW 1: Title & Subtitle */}
          <div className="content-stretch flex flex-col gap-[20px] items-start relative shrink-0 w-full">
            <div className="content-stretch flex gap-[20px] items-center relative shrink-0 w-full">
              <Wrapper1>
                <div className="content-stretch flex flex-col items-start relative shrink-0">
                  <div style={getPreviewStyle(typography.title)}>
                    <Wrapper5 additionalClassNames="text-[24px]">Title</Wrapper5>
                  </div>
                </div>
                <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                  <FontFamilySelector
                    font={typography.title.fontFamily}
                    onChange={(f) => handleTypographyChange('title', { fontFamily: f })}
                    triggerComponent={<div className="cursor-pointer"><Text text={typography.title.fontFamily} additionalClassNames="w-[190px]" /></div>}
                  />
                  <FontSizeSelector size={typography.title.fontSize} onChange={(s) => handleTypographyChange('title', { fontSize: s })} />
                  <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                    <Helper
                      isActive={!typography.title.isItalic && !typography.title.isUppercase}
                      onClick={() => handleTypographyChange('title', { isBold: !typography.title.isBold })}
                    />
                    <Helper1
                      isActive={typography.title.isItalic}
                      onClick={() => handleTypographyChange('title', { isItalic: !typography.title.isItalic })}
                    />
                    <Wrapper4
                      isActive={typography.title.isUppercase}
                      onClick={() => handleTypographyChange('title', { isUppercase: !typography.title.isUppercase })}
                    >
                      <div className="content-stretch flex items-center relative shrink-0">
                        <Frame1 />
                      </div>
                    </Wrapper4>
                  </div>
                </div>
              </Wrapper1>
              <Wrapper1>
                <div className="content-stretch flex flex-col items-start relative shrink-0">
                  <div style={getPreviewStyle(typography.subtitle)}>
                    <Wrapper5 additionalClassNames="text-[20px]">{`Subtitle `}</Wrapper5>
                  </div>
                </div>
                <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                  <FontFamilySelector
                    font={typography.subtitle.fontFamily}
                    onChange={(f) => handleTypographyChange('subtitle', { fontFamily: f })}
                    triggerComponent={<div className="cursor-pointer"><Text text={typography.subtitle.fontFamily} additionalClassNames="w-[190px]" /></div>}
                  />
                  <FontSizeSelector size={typography.subtitle.fontSize} onChange={(s) => handleTypographyChange('subtitle', { fontSize: s })} />
                  <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                    <Helper
                      isActive={!typography.subtitle.isItalic && !typography.subtitle.isUppercase}
                      onClick={() => handleTypographyChange('subtitle', { isBold: !typography.subtitle.isBold })}
                    />
                    <Helper1
                      isActive={typography.subtitle.isItalic}
                      onClick={() => handleTypographyChange('subtitle', { isItalic: !typography.subtitle.isItalic })}
                    />
                    <Wrapper4
                      isActive={typography.subtitle.isUppercase}
                      onClick={() => handleTypographyChange('subtitle', { isUppercase: !typography.subtitle.isUppercase })}
                    >
                      <div className="content-stretch flex items-center relative shrink-0">
                        <Frame1 />
                      </div>
                    </Wrapper4>
                  </div>
                </div>
              </Wrapper1>
            </div>

            {/* ROW 2: Heading & Subheading */}
            <div className="content-stretch flex gap-[20px] items-center relative shrink-0 w-full">
              <Wrapper1>
                <div style={getPreviewStyle(typography.heading)}><Text1 text="Heading" /></div>
                <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                  <FontFamilySelector
                    font={typography.heading.fontFamily}
                    onChange={(f) => handleTypographyChange('heading', { fontFamily: f })}
                    triggerComponent={<div className="cursor-pointer"><Text2 text={typography.heading.fontFamily} /></div>}
                  />
                  <FontSizeSelector size={typography.heading.fontSize} onChange={(s) => handleTypographyChange('heading', { fontSize: s })} />
                  <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                    <Helper
                      isActive={!typography.heading.isItalic && !typography.heading.isUppercase}
                      onClick={() => handleTypographyChange('heading', { isBold: !typography.heading.isBold })}
                    />
                    <Helper1
                      isActive={typography.heading.isItalic}
                      onClick={() => handleTypographyChange('heading', { isItalic: !typography.heading.isItalic })}
                    />
                    <Wrapper4
                      isActive={typography.heading.isUppercase}
                      onClick={() => handleTypographyChange('heading', { isUppercase: !typography.heading.isUppercase })}
                    >
                      <div className="content-stretch flex items-center relative shrink-0">
                        <Frame1 />
                      </div>
                    </Wrapper4>
                  </div>
                </div>
              </Wrapper1>
              <Wrapper1>
                <div className="content-stretch flex flex-col items-start relative shrink-0">
                  <div style={getPreviewStyle(typography.subheading)} className="flex flex-col font-sans font-semibold justify-center leading-[0] relative shrink-0 text-[#101928] text-[16px] w-full">
                    <p className="leading-[1.8]">Subheading</p>
                  </div>
                </div>
                <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                  <FontFamilySelector
                    font={typography.subheading.fontFamily}
                    onChange={(f) => handleTypographyChange('subheading', { fontFamily: f })}
                    triggerComponent={<div className="cursor-pointer"><Text2 text={typography.subheading.fontFamily} /></div>}
                  />
                  <FontSizeSelector size={typography.subheading.fontSize} onChange={(s) => handleTypographyChange('subheading', { fontSize: s })} />
                  <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                    <Helper
                      isActive={!typography.subheading.isItalic && !typography.subheading.isUppercase}
                      onClick={() => handleTypographyChange('subheading', { isBold: !typography.subheading.isBold })}
                    />
                    <Helper1
                      isActive={typography.subheading.isItalic}
                      onClick={() => handleTypographyChange('subheading', { isItalic: !typography.subheading.isItalic })}
                    />
                    <Wrapper4
                      isActive={typography.subheading.isUppercase}
                      onClick={() => handleTypographyChange('subheading', { isUppercase: !typography.subheading.isUppercase })}
                    >
                      <div className="content-stretch flex items-center relative shrink-0">
                        <Frame1 />
                      </div>
                    </Wrapper4>
                  </div>
                </div>
              </Wrapper1>
            </div>

            {/* ROW 3: Geologica (Small Heading) & Body */}
            <div className="content-stretch flex gap-[20px] items-center relative shrink-0 w-full">
              <Wrapper1>
                <div style={getPreviewStyle(typography.smallHeading)}><Text1 text="Small Heading" /></div>
                <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                  <FontFamilySelector
                    font={typography.smallHeading.fontFamily}
                    onChange={(f) => handleTypographyChange('smallHeading', { fontFamily: f })}
                    triggerComponent={
                      <Wrapper3 onClick={() => { }}>
                        <Text3 text={typography.smallHeading.fontFamily} additionalClassNames="font-sans font-normal text-nowrap" />
                        <Frame />
                      </Wrapper3>
                    }
                  />
                  <FontSizeSelector size={typography.smallHeading.fontSize} onChange={(s) => handleTypographyChange('smallHeading', { fontSize: s })} />
                  <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                    <Helper
                      isActive={!typography.smallHeading.isItalic && !typography.smallHeading.isUppercase}
                      onClick={() => handleTypographyChange('smallHeading', { isBold: !typography.smallHeading.isBold })}
                    />
                    <Helper1
                      isActive={typography.smallHeading.isItalic}
                      onClick={() => handleTypographyChange('smallHeading', { isItalic: !typography.smallHeading.isItalic })}
                    />
                    <Wrapper4
                      isActive={typography.smallHeading.isUppercase}
                      onClick={() => handleTypographyChange('smallHeading', { isUppercase: !typography.smallHeading.isUppercase })}
                    >
                      <div className="content-stretch flex items-center relative shrink-0">
                        <Frame1 />
                      </div>
                    </Wrapper4>
                  </div>
                </div>
              </Wrapper1>
              <Wrapper1>
                <div className="content-stretch flex flex-col items-start relative shrink-0">
                  <div style={getPreviewStyle(typography.body)}>
                    <Text3 text="Body" additionalClassNames="font-sans font-semibold w-full" />
                  </div>
                </div>
                <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                  <FontFamilySelector
                    font={typography.body.fontFamily}
                    onChange={(f) => handleTypographyChange('body', { fontFamily: f })}
                    triggerComponent={<div className="cursor-pointer"><Text2 text={typography.body.fontFamily} /></div>}
                  />
                  <FontSizeSelector size={typography.body.fontSize} onChange={(s) => handleTypographyChange('body', { fontSize: s })} />
                  <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                    <Helper
                      isActive={!typography.body.isItalic && !typography.body.isUppercase}
                      onClick={() => handleTypographyChange('body', { isBold: !typography.body.isBold })}
                    />
                    <Helper1
                      isActive={typography.body.isItalic}
                      onClick={() => handleTypographyChange('body', { isItalic: !typography.body.isItalic })}
                    />
                    <Wrapper4
                      isActive={typography.body.isUppercase}
                      onClick={() => handleTypographyChange('body', { isUppercase: !typography.body.isUppercase })}
                    >
                      <div className="content-stretch flex items-center relative shrink-0">
                        <Frame1 />
                      </div>
                    </Wrapper4>
                  </div>
                </div>
              </Wrapper1>
            </div>

            {/* ROW 4: Quote & Caption */}
            <div className="content-stretch flex gap-[20px] items-center relative shrink-0 w-full">
              <Wrapper1>
                <div className="content-stretch flex flex-col items-start relative shrink-0">
                  <div style={getPreviewStyle(typography.quote)} className="flex flex-col font-sans font-medium italic justify-center leading-[0] relative shrink-0 text-[#101928] text-[18px] w-full">
                    <p className="leading-[1.8]">Quote</p>
                  </div>
                </div>
                <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                  <FontFamilySelector
                    font={typography.quote.fontFamily}
                    onChange={(f) => handleTypographyChange('quote', { fontFamily: f })}
                    triggerComponent={
                      <Wrapper3 onClick={() => { }}>
                        <div className="flex flex-col font-sans font-medium italic justify-center leading-[0] relative shrink-0 text-[#101928] text-[16px] text-nowrap">
                          <p className="leading-[1.8]">{typography.quote.fontFamily}</p>
                        </div>
                        <Frame />
                      </Wrapper3>
                    }
                  />
                  <FontSizeSelector size={typography.quote.fontSize} onChange={(s) => handleTypographyChange('quote', { fontSize: s })} />
                  <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                    <Helper
                      isActive={!typography.quote.isItalic && !typography.quote.isUppercase}
                      onClick={() => handleTypographyChange('quote', { isBold: !typography.quote.isBold })}
                    />
                    <Helper1
                      isActive={typography.quote.isItalic}
                      onClick={() => handleTypographyChange('quote', { isItalic: !typography.quote.isItalic })}
                    />
                    <Wrapper4
                      isActive={typography.quote.isUppercase}
                      onClick={() => handleTypographyChange('quote', { isUppercase: !typography.quote.isUppercase })}
                    >
                      <div className="content-stretch flex items-center relative shrink-0">
                        <Frame1 />
                      </div>
                    </Wrapper4>
                  </div>
                </div>
              </Wrapper1>
              <Wrapper1>
                <div className="content-stretch flex flex-col items-start relative shrink-0">
                  <div
                    style={{ ...getPreviewStyle(typography.caption), fontVariationSettings: "'wdth' 100" }}
                    className="flex flex-col font-sans font-semibold justify-center leading-[0] relative shrink-0 text-[#101928] text-[16px] w-full"
                  >
                    <p className="leading-[1.8]">Caption</p>
                  </div>
                </div>
                <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                  <FontFamilySelector
                    font={typography.caption.fontFamily}
                    onChange={(f) => handleTypographyChange('caption', { fontFamily: f })}
                    triggerComponent={<div className="cursor-pointer"><Text2 text={typography.caption.fontFamily} /></div>}
                  />
                  <FontSizeSelector size={typography.caption.fontSize} onChange={(s) => handleTypographyChange('caption', { fontSize: s })} />
                  <div className="content-stretch flex gap-[20px] items-center relative shrink-0">
                    <Helper
                      isActive={!typography.caption.isItalic && !typography.caption.isUppercase}
                      onClick={() => handleTypographyChange('caption', { isBold: !typography.caption.isBold })}
                    />
                    <Helper1
                      isActive={typography.caption.isItalic}
                      onClick={() => handleTypographyChange('caption', { isItalic: !typography.caption.isItalic })}
                    />
                    <Wrapper4
                      isActive={typography.caption.isUppercase}
                      onClick={() => handleTypographyChange('caption', { isUppercase: !typography.caption.isUppercase })}
                    >
                      <div className="content-stretch flex items-center relative shrink-0">
                        <Frame1 />
                      </div>
                    </Wrapper4>
                  </div>
                </div>
              </Wrapper1>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
